import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditOutlined, FieldTimeOutlined, FileSyncOutlined, GroupOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Modal, notification, Select, TableProps, Tabs, Tag, Tooltip, Typography } from "antd";
import STableLayout, {
    DEFAULT_PAGE_SIZE,
    TableLayoutRef,
} from "@/styles/STableLayout";
import apis, { QuotationRequestInts } from "@/apis/requirement";
import useDataList from "@/hooks/useDataList";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { useTranslation } from "react-i18next";
import SText from "@/styles/SText";
import { QuotationRequest, QuotationRequestStatusEnum } from "@/types/requirement";
import { CommonListParams, ItemLabelEnum } from "@/types/common";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import usePage from "@/layout/hooks/usePage";
import { getSupplierName } from "@/utils/supplier";
import { PurchaseQuotationRequest } from "@/types/purchase";
import LazyQuate from "../Detail/components/LazyQuate";
import CountdownTimer from "@/SupplierPages/Home/Request/components/CountdownTimer";
import QuotationForm from "@/components/QuotationForm";
import RequirementDetail from "../Detail";
import { Requirement } from "@/types/requirement";
import { getUserAvatarTip, getUserName, hasPermission } from "@/utils/user";
import { User, UserPermissionEnum } from "@/types/user";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { TimeSelector, TimeSelectorRef } from "./components/TimeSelector";
import { quotationRequestDetail, quotationRequestSubmit, QuotationRequestSubmit, quotationRequestUpdate } from "@/apis/quotationRequestItem";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import WmsTags from "@/components/WmsTags";
import { getQuotationPrice, numFormmater } from "../../util";
import { getSuppLadderPrice } from "@/utils/getSuppLadderPrice";
import { QuotationRequestItem } from "@/types/quotationRequestItem";
import { divided, multi } from "@/utils/calc";
import { SConModal } from "@/styles/SConModal";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import WMSAvatar from "@/styles/WMSAvatar";

enum SelectEnum {
    UN_PRICED = 1,
    PRICED = 2,
    REJECTED = 3,
}

interface InquiryFilters {
    keyword: string;
    statusInts: SelectEnum | null;
}

const INITIAL_INQUIRY_FILTERS: InquiryFilters = {
    keyword: "",
    statusInts: null,
};

const formatQuotationNumber = (value?: number | string | null) => {
    if (value === undefined || value === null || value === "") return "";

    const valueNumber = Number(value);
    return Number.isFinite(valueNumber) ? numFormmater(valueNumber) : String(value);
};

const getQuotationSubmitPatch = (submitData: QuotationRequestSubmit): Partial<PurchaseQuotationRequest> => {
    const { mpq, spareCount, ...patch } = submitData;

    return {
        ...patch,
        mpq: mpq === null ? null : mpq + '',
        spareCount: Number(spareCount || '0'),
    };
};

// 批量报价只允许处理未报价问询单；统一入口避免表格禁用态和选中清理规则分叉。
const selectableQuotationStatuses = [QuotationRequestStatusEnum.READ, QuotationRequestStatusEnum.UNREAD];

const isSelectableQuotation = (record?: Pick<PurchaseQuotationRequest | QuotationRequest, "status"> | null) => (
    !!record && selectableQuotationStatuses.includes(record.status)
);

// 勾选状态不改变业务 record；用 shouldCellUpdate 让 selection 列变化时业务列尽量不重渲染。
const shouldQuotationCellUpdate = (record: PurchaseQuotationRequest, prevRecord: PurchaseQuotationRequest) => record !== prevRecord;

type QuotationColumn = NonNullable<TableProps<PurchaseQuotationRequest>["columns"]>[number];

const withQuotationCellUpdate = (columns: QuotationColumn[]): QuotationColumn[] => {
    return columns.map(column => {
        const nextColumn = {
            ...column,
            shouldCellUpdate: column.shouldCellUpdate ?? shouldQuotationCellUpdate,
        };

        if ("children" in column && column.children) {
            return {
                ...nextColumn,
                children: withQuotationCellUpdate(column.children as QuotationColumn[]),
            };
        }

        return nextColumn;
    });
};

const RequirementList: FC = () => {
    const { t } = useTranslation();
    const { pageId } = usePage()
    const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);

    const timeSelectorRef = useRef<TimeSelectorRef>(null)

    const [activeTab, setActiveTab] = useState<QuotationRequestInts>(QuotationRequestInts.MANUAL);

    const [list, total, listLoading, { fetchList, setList, rmItem, rmItems }] =
        useDataList<PurchaseQuotationRequest, [string, CommonListParams]>({ fetchApi: apis.quotationListIsSup });
    const [listTimed, totalTimed, listLoadingTimed, { fetchList: fetchListTimed, setList: setListTimed }] =
        useDataList<PurchaseQuotationRequest, [string, CommonListParams]>({ fetchApi: apis.quotationListIsSupTimed });

    const tableRef = useRef<TableLayoutRef | null>(null);
    const {
        draftFilters,
        appliedFilters,
        updateDraftFilters,
        commitFilters,
        replaceFilters,
    } = useCommittedFilters<InquiryFilters>(INITIAL_INQUIRY_FILTERS);
    const { keyword, statusInts } = draftFilters;
    const appliedStatusInts = appliedFilters.statusInts;
    // 展示和行更新使用同一个 Hook，草稿状态变化不切换列表数据源。
    const isRejected = appliedStatusInts === SelectEnum.REJECTED;
    const qutationList = isRejected ? listTimed : list;
    const setQutationList = isRejected ? setListTimed : setList;
    const activeListTotal = isRejected ? totalTimed : total;
    const activeListLoading = isRejected ? listLoadingTimed : listLoading;
    const [qutation, setQutation] = useState<PurchaseQuotationRequest | null>(null);
    const [detailId, setDetailId] = useState<string | undefined>();
    const [lazyMrdDetail, setLazyMrdDetail] = useState<PurchaseQuotationRequest | null>(null);
    const [mrdLoading, setMrdLoading] = useState<boolean>(false);
    const [quoDetail, setQuoDetail] = useState<QuotationRequest | null>(null)
    const [exportLoading, setExportLoading] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [requirementDetail, setRequirementDetail] = useState<Requirement | null>(null)

    // 多选相关 state
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [submiting, setSubmiting] = useState(false);
    const [submitProcess, setSubmitProcess] = useState(0);

    // 筛选条件由调用方显式传入，列表状态由对应 Hook 维护。
    const fetch = useCallback((isSupplier: QuotationRequestInts, offset: number, filters: InquiryFilters) => {
        const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
        if (filters.statusInts === SelectEnum.REJECTED) {
            fetchListTimed(isSupplier + '', {
                pagination: { offset, take },
                body: filters.keyword,
            })
                .catch((err) => {
                    switch (err) {
                        case 10010:
                            break;
                        default:
                            break;
                    }
                });
        } else {
            fetchList(isSupplier + '', {
                pagination: { offset, take },
                body: filters.keyword,
                ints: filters.statusInts ?
                    filters.statusInts === SelectEnum.PRICED ? [QuotationRequestStatusEnum.SUBMITED]
                        : filters.statusInts === SelectEnum.UN_PRICED ? [QuotationRequestStatusEnum.READ, QuotationRequestStatusEnum.UNREAD]
                            : undefined
                    : undefined,
            })
                .catch((err) => {
                    switch (err) {
                        case 10010:
                            break;
                        default:
                            break;
                    }
                });
        }
    }, [fetchList, fetchListTimed]);

    // 切换查询范围时只清空 rowKey，不保存或清理完整行对象。
    const clearSelectedRowKeys = useCallback(() => {
        setSelectedRowKeys([]);
    }, []);

    useEffect(() => {
        const nextFilters = replaceFilters({
            ...INITIAL_INQUIRY_FILTERS,
            statusInts: appliedFilters.statusInts,
        });
        clearSelectedRowKeys();
        if (pageId) {
            fetch(activeTab, 0, nextFilters);
        }
        tableRef.current?.resetTablePage();
        // 页面或 Tab 切换时清空关键词、保留已生效状态；筛选提交不触发此 effect。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageId, activeTab, clearSelectedRowKeys]);

    // 导出全部数据
    const handleExportAll = useCallback(async (): Promise<PurchaseQuotationRequest[]> => {
        setExportLoading(true);
        setExportProgress(0);
        const allDataList: PurchaseQuotationRequest[] = [];

        try {
            const batchSize = 200;
            const { keyword: appliedKeyword, statusInts: appliedStatus } = appliedFilters;
            const apiToUse = appliedStatus === SelectEnum.REJECTED ? apis.quotationListIsSupTimed : apis.quotationListIsSup;

            const firstRes = await apiToUse(activeTab + '', {
                pagination: { offset: 0, take: batchSize },
                body: appliedKeyword,
                ints: appliedStatus ?
                    appliedStatus === SelectEnum.PRICED ? [QuotationRequestStatusEnum.SUBMITED]
                        : appliedStatus === SelectEnum.UN_PRICED ? [QuotationRequestStatusEnum.READ, QuotationRequestStatusEnum.UNREAD]
                            : undefined
                    : undefined,
            });

            if (firstRes?.body) {
                allDataList.push(...firstRes.body);
            }

            const totalSize = firstRes?.pagination?.size ?? 0;
            const batches = Math.ceil(totalSize / batchSize);

            for (let i = 1; i < batches; i++) {
                const offset = i * batchSize;
                const res = await apiToUse(activeTab + '', {
                    pagination: { offset, take: batchSize },
                    body: appliedKeyword,
                    ints: appliedStatus ?
                        appliedStatus === SelectEnum.PRICED ? [QuotationRequestStatusEnum.SUBMITED]
                            : appliedStatus === SelectEnum.UN_PRICED ? [QuotationRequestStatusEnum.READ, QuotationRequestStatusEnum.UNREAD]
                                : undefined
                        : undefined,
                });
                if (res?.body) {
                    allDataList.push(...res.body);
                }
                setExportProgress(Math.round(((i + 1) / batches) * 100));
            }
            return allDataList;
        } catch (error) {
            console.error(t('common.exportErr'), error);
            throw error;
        } finally {
            setExportLoading(false);
            setExportProgress(0);
        }
    }, [activeTab, appliedFilters, t]);

    // 多选配置
    const handleSearch = useCallback(() => {
        // 搜索后列表范围会变化，旧选中 id 不能跨查询条件继续保留。
        const nextFilters = commitFilters();
        clearSelectedRowKeys();
        fetch(activeTab, 0, nextFilters);
        tableRef?.current?.resetTablePage();
    }, [activeTab, clearSelectedRowKeys, commitFilters, fetch]);

    const handleClearFilters = useCallback(() => {
        const nextFilters = replaceFilters(INITIAL_INQUIRY_FILTERS);
        clearSelectedRowKeys();
        fetch(activeTab, 0, nextFilters);
        tableRef.current?.resetTablePage();
    }, [activeTab, clearSelectedRowKeys, fetch, replaceFilters]);

    const handleStatusChange = useCallback((val: SelectEnum | null) => {
        updateDraftFilters({ statusInts: val ?? null });
    }, [updateDraftFilters]);

    const handleTableChange = useCallback((offset: number) => {
        fetch(activeTab, offset, appliedFilters);
    }, [activeTab, appliedFilters, fetch]);

    const handleTabChange = useCallback((tab: string) => {
        const nextTab = Number(tab) as QuotationRequestInts;
        setActiveTab(nextTab);
    }, []);

    // rowSelection 稳定后，勾选只更新 selectedRowKeys，不额外重建整套表格配置。
    const rowSelection: TableProps<AnyObject>['rowSelection'] = useMemo(() => ({
        selectedRowKeys,
        onChange: (keys) => {
            setSelectedRowKeys(keys as string[]);
        },
        columnWidth: 50,
        fixed: 'left',
        // 批量提交按 id 拉详情，允许同一查询条件下跨页保留选中项。
        preserveSelectedRowKeys: true,
        getTitleCheckboxProps: () => ({
            disabled: submiting,
        }),
        getCheckboxProps(record) {
            return {
                // 已提交/已拒绝或提交中的问询单不可选，避免批量提交拿到无效 id。
                disabled: submiting || !isSelectableQuotation(record as QuotationRequest),
            }
        },
    }), [selectedRowKeys, submiting]);

    const tableTabs = useMemo(() => [
        {
            key: QuotationRequestInts.MANUAL + '',
            label: t("requirement.requirementHelp"),
        },
        {
            key: QuotationRequestInts.AUTO + '',
            label: t("requirement.supplierQuote"),
        },
    ], [t]);

    // 导出列配置
    const exportColumns = useCallback((t: any) => [
        { key: 'purchase.code', label: t('purchase.code') },
        { key: 'requirement.supplier', label: t('requirement.supplier') },
        { key: 'product.code', label: t('product.code') },
        { key: 'product.name', label: t('product.name') },
        { key: 'product.brand1', label: t('product.brand1') },
        { key: 'product.brand2', label: t('product.brand2') },
        { key: 'product.uom', label: t('product.uom') },
        { key: 'common.desc', label: t('common.desc') },
        { key: 'purchase.purCount', label: t('purchase.purCount') },
        { key: 'requirement.prevDMrd', label: t('requirement.prevDMrd') },
        { key: 'requirement.supplierTime', label: t('requirement.supplierTime') },
        { key: 'supplierQuotation.deadline', label: t('supplierQuotation.deadline') },
        { key: 'purchase.purPrice', label: t('purchase.purPrice') },
        { key: 'supplierQuotation.handlePrice', label: t('supplierQuotation.handlePrice') },
        { key: 'requirement.mpq', label: t('requirement.mpq') },
        { key: 'requirement.days', label: t('requirement.days') },
        { key: 'purchase.spareCount', label: t('purchase.spareCount') },
        { key: 'purchase.remark', label: t('purchase.remark') },
        { key: 'requirement.status', label: t('requirement.status') },
        { key: 'sale.requirement', label: t('sale.requirement') },
        { key: 'purchase.brand', label: t('purchase.brand') },
        { key: 'purchase.mpn', label: t('purchase.mpn') },
        { key: 'purchase.dcc', label: t('purchase.dcc') },
    ], []);

    // 默认选中的列
    const defaultSelectedColumns = useCallback((t: any) => [
        'purchase.code',
        'requirement.supplier',
        'product.code',
        'product.name',
        'purchase.purCount',
        'requirement.status',
    ], []);

    // 导出数据映射函数
    const exportDataMapper = useCallback((item: PurchaseQuotationRequest, t: any) => {
        const isTimedOut = !!item?.deadline && item.deadline < Date.now();
        let statusText = '';
        if (isTimedOut && [QuotationRequestStatusEnum.READ, QuotationRequestStatusEnum.UNREAD].includes(item.status)) {
            statusText = t("requirement.timedOut");
        } else if (item.status === QuotationRequestStatusEnum.READ || item.status === QuotationRequestStatusEnum.UNREAD) {
            statusText = t("requirement.unPriced");
        } else if (item.status === QuotationRequestStatusEnum.SUBMITED) {
            statusText = t("requirement.resolved");
        } else if (item.status === QuotationRequestStatusEnum.REJECTED) {
            statusText = t("requirement.reject");
        }

        return {
            [t('purchase.code')]: item?.supplier?.code || '',
            [t('requirement.supplier')]: getSupplierName(item.supplier),
            [t('product.code')]: item?.requirementItem?.product?.code || '',
            [t('product.name')]: item?.requirementItem?.product?.name || '',
            [t('product.brand1')]: item?.requirementItem?.product?.model || '',
            [t('product.brand2')]: item?.requirementItem?.product?.t1 || '',
            [t('product.uom')]: item?.requirementItem?.product?.uom || '',
            [t('common.desc')]: item?.requirementItem?.product?.desc || '',
            [t('purchase.purCount')]: item?.count || 0,
            [t('requirement.prevDMrd')]: item?.mrd ? dateFormat(dayjs(item.mrd)) : '',
            [t('requirement.supplierTime')]: item?.supplierTime ? dateFormat(dayjs(item.supplierTime)) : '',
            [t('supplierQuotation.deadline')]: item?.deadline ? dateFormat(dayjs(item.deadline)) : '',
            [t('purchase.purPrice')]: item ? (getQuotationPrice(item) ?? '') : '',
            [t('supplierQuotation.handlePrice')]: formatQuotationNumber(item?.supplierPrice),
            [t('requirement.mpq')]: item?.mpq || '',
            [t('requirement.days')]: item?.days?.toString() || '',
            [t('purchase.spareCount')]: formatQuotationNumber(item?.spareCount),
            [t('purchase.remark')]: item?.remark || '',
            [t('requirement.status')]: statusText,
            [t('sale.requirement')]: item?.requirementItem?.requirement?.number || '',
            [t('purchase.brand')]: item?.price?.brand ?? '',
            [t('purchase.mpn')]: item?.price?.mpn ?? '',
            [t('purchase.dcc')]: String(item?.price?.dcc ?? ''),
        };
    }, []);

    // 顶部工具栏保持 memo，勾选变化时不重建导出、筛选、搜索控件。
    const headerChildren = useMemo(() => (
        <Flex justify="space-between" style={{ width: "100%" }} gap={10} align="baseline" >
            <Tabs
                activeKey={activeTab + ''}
                onChange={handleTabChange}
                type="card"
                items={tableTabs}
            />
            <SConModal disable={selectedRowKeys.length === 0} title={t('purchase.batchSubmit')} content={t('purchase.batchSubmitTip')} onOk={() => batchSubmit()}>
                <Button disabled={selectedRowKeys.length === 0} loading={submiting}>{t('purchase.batchSubmit')}</Button>
            </SConModal>
            <Flex justify="flex-end" gap={10} align="center" style={{ flex: 1 }}>
                <ExportExcelButton<PurchaseQuotationRequest>
                    list={qutationList}
                    columns={exportColumns}
                    defaultSelectedColumns={defaultSelectedColumns}
                    dataMapper={exportDataMapper}
                    fileName="quotation_list.xlsx"
                    sheetName={t('layout.QutationManage')}
                />
                <ExportExcelButton<PurchaseQuotationRequest>
                    list={qutationList}
                    isAll
                    loadData={handleExportAll}
                    columns={exportColumns}
                    defaultSelectedColumns={defaultSelectedColumns}
                    dataMapper={exportDataMapper}
                    fileName="quotation_list_all.xlsx"
                    sheetName={t('layout.QutationManage')}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <ListFilterBar
                        onSearch={handleSearch}
                        onReset={handleClearFilters}
                        disabled={activeListLoading}
                    >
                        {({ submit }) => (
                            <>
                                <Select
                                    style={{ width: 120 }}
                                    placeholder={t('requirement.status')}
                                    size="small"
                                    options={[
                                        { label: t('requirement.unPriced'), value: SelectEnum.UN_PRICED, key: 0 },
                                        { label: t('requirement.resolved'), value: SelectEnum.PRICED, key: 1 },
                                        { label: t('supplierLayout.other'), value: SelectEnum.REJECTED, key: 2 },
                                    ]}
                                    value={statusInts}
                                    onChange={handleStatusChange}
                                    allowClear
                                />
                                <Tooltip title={t("requirement.searchPlaceHolder")}>
                                    <Input
                                        placeholder={t("requirement.searchPlaceHolder")}
                                        style={{ width: 120 }}
                                        value={keyword}
                                        onChange={event =>
                                            updateDraftFilters({ keyword: event.target.value.trim() })
                                        }
                                        onPressEnter={submit}
                                    />
                                </Tooltip>
                            </>
                        )}
                    </ListFilterBar>
                </div>
            </Flex>
        </Flex>

    ), [activeListLoading, activeTab, defaultSelectedColumns, exportColumns, exportDataMapper, handleClearFilters, handleExportAll, handleSearch, handleStatusChange, handleTabChange, keyword, qutationList, statusInts, t, tableTabs, updateDraftFilters]);

    const getStatusLabel = useCallback((status: QuotationRequestStatusEnum, timed = false) => {
        if (timed && [QuotationRequestStatusEnum.READ, QuotationRequestStatusEnum.UNREAD].includes(status)) return <Tag color="#f50">{t("requirement.timedOut")}</Tag>
        return status === QuotationRequestStatusEnum.READ ||
            status === QuotationRequestStatusEnum.UNREAD ? (
            <Tag color="#2db7f5">{t("requirement.unPriced")}</Tag>
        ) : status === QuotationRequestStatusEnum.SUBMITED ? (
            <Tag color="#87d068">{t("requirement.resolved")}</Tag>
        ) : status === QuotationRequestStatusEnum.REJECTED ? (
            <Tag color="#f50">{t("requirement.reject")}</Tag>
        ) : (
            <></>
        );
    }, [t]);

    // 给所有业务列补 shouldCellUpdate，避免 checkbox 勾选带动整张表重绘。
    const columns: TableProps<PurchaseQuotationRequest>["columns"] = useMemo(() => withQuotationCellUpdate([
        {
            title: t("purchase.code"),
            dataIndex: ["supplier", "code"],
            width: 120,
            fixed: 'left',
            sorter: (a, b) => {
                const valA = a?.supplier?.code ?? "";
                const valB = b?.supplier?.code ?? "";
                return valA.localeCompare(valB);
            },
            render: (_, record) => <SText text={record?.supplier?.code} style={{ width: "100%" }} />,
        },
        {
            title: t("requirement.supplier"),
            dataIndex: "supplerId",
            fixed: 'left',
            render: (_, record) => (
                <SText
                    text={getSupplierName(record.supplier)}
                    style={{ width: "100%" }}
                />
            ),
        },
        {
            title: t("purchase.productInfo"),
            children: [
                {
                    title: <SText text={t("product.code")} />,
                    dataIndex: ["requirementItem", "product", "code"],
                    width: 100,
                    sorter: (a, b) => {
                        const valA = a?.requirementItem?.product?.code ?? "";
                        const valB = b?.requirementItem?.product?.code ?? "";
                        return valA.localeCompare(valB);
                    },
                    render: (_, item) => <SText text={item?.requirementItem?.product?.code ?? ''} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("product.name")} />,
                    dataIndex: "name",
                    width: 100,
                    render: (_, item) => <SText text={item?.requirementItem?.product?.name ?? ''} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("product.brand1")} />,
                    dataIndex: "model1",
                    width: 120,
                    render: (_, item) => <SText text={item?.requirementItem?.product?.model ?? ''} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("product.brand2")} />,
                    dataIndex: "model2",
                    width: 120,
                    render: (_, item) => <SText text={item?.requirementItem?.product?.t1 ?? ''} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("purchase.brand")} />,
                    dataIndex: "brand",
                    width: 120,
                    render: (_, item) => <SText text={item?.price?.brand ?? ''} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("purchase.mpn")} />,
                    dataIndex: "mpn",
                    width: 120,
                    render: (_, item) => <SText text={item?.price?.mpn ?? ''} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("purchase.dcc")} />,
                    dataIndex: "dcc",
                    width: 120,
                    render: (_, item) => <SText text={String(item?.price?.dcc ?? '')} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("product.uom")} />,
                    dataIndex: "uom",
                    width: 80,
                    render: (_, item) => <SText text={item?.requirementItem?.product?.uom ?? ''} style={{ width: "100%" }} />,
                },
                {
                    title: <SText text={t("common.desc")} />,
                    dataIndex: "desc",
                    width: 120,
                    render: (_, item) => <SText text={item?.requirementItem?.product?.desc ?? ''} style={{ width: "100%" }} />,
                },
                // {
                //     title: <SText text={t("purchase.stPrice")} />,
                //     dataIndex: "price",
                //     width: 80,
                //     render: (_, item) => <SText text={item?.requirementItem?.price?.price ?? ''} style={{ width: "100%" }} />,
                // },
            ]
        },
        {
            title: t("purchase.purCount"),
            dataIndex: "count",
            sorter: (a, b) => (a?.count ?? 0) - (b?.count ?? 0),
            render: (_, record) => <SText text={numFormmater(record.count)} style={{ width: "100%" }} />
        },
        {
            title: t("requirement.prevDMrd"),
            dataIndex: "mrd",
            width: 140,
            render: (_, record) => (
                <SText
                    text={
                        record?.mrd ? dateFormat(dayjs(record.mrd)) : ""
                    }
                    style={{ width: "100%" }}
                />
            ),
        },
        {
            title: t("requirement.supplierTime"),
            dataIndex: "supplierTime",
            width: 140,
            render: (_, record) => (
                <SText
                    text={
                        record?.supplierTime ? dateFormat(dayjs(record.supplierTime)) : ""
                    }
                    style={{ width: "100%" }}
                />
            ),
        },
        {
            title: t("supplierQuotation.deadline"),
            dataIndex: "deadline",
            width: 140,
            render: (_, record) => (
                <SText
                    text={
                        record?.deadline ? dateFormat(dayjs(record.deadline)) : ""
                    }
                    style={{ width: "100%" }}
                />
            ),
        },
        {
            title: t("purchase.purPrice"),
            dataIndex: "price",
            width: 120,
            render: (_, record) => {
                const price = getQuotationPrice(record);
                return <SText text={price === null ? "" : numFormmater(price)} style={{ width: "100%" }} />;
            },
        },
        {
            title: t("supplierQuotation.handlePrice"),
            dataIndex: "supplierPrice",
            width: 120,
            render: (_, record) => (
                <SText text={formatQuotationNumber(record.supplierPrice)} style={{ width: "100%" }} />
            ),
        },
        {
            title: t("requirement.mpq"),
            dataIndex: "mpq",
            render: (_, record) => (
                <SText text={record.mpq || ""} style={{ width: "100%" }} />
            ),
        },
        {
            title: t("requirement.days"),
            dataIndex: "days",
            render: (_, record) => (
                <SText
                    text={record?.days?.toString() || ""}
                    style={{ width: "100%" }}
                />
            ),
        },
        {
            title: t("purchase.spareCount"),
            dataIndex: "spareCount",
            width: 100,
            render: (_, record) => (
                <SText text={formatQuotationNumber(record?.spareCount)} style={{ width: "100%" }} />
            ),
        },
        {
            title: t("purchase.remark"),
            dataIndex: "remark",
            width: 160,
            render: (_, record) => (
                <SText text={record?.remark || ""} style={{ width: "100%" }} />
            ),
        },
        {
            title: <SText text={t("common.creator")} />,
            dataIndex: "creator",
            width: 100,
            render: (_, item) => {
                let userData = item.creator;
                return (
                    userData && <Flex>
                        <div>
                            <WMSAvatar creator={userData} uniChar={userData.id}
                                avatarId={userData.avatar}
                                size={24} alt={getUserAvatarTip(userData)}
                            />
                        </div>
                        <SText text={getUserName(userData)} />
                    </Flex>
                )
            },
        },
        {
            title: t("requirement.status"),
            dataIndex: "status",
            fixed: "right",
            render: (_, record) => getStatusLabel(record.status, !!record?.deadline && record.deadline < Date.now())
        },
        {
            title: t("supplierQuotation.submitTimer"),
            width: 100,
            fixed: "right",
            dataIndex: "deadline",
            render: (_, record) => (
                [QuotationRequestStatusEnum.READ, QuotationRequestStatusEnum.UNREAD].includes(record.status) &&
                <CountdownTimer
                    onDue={() => { }}
                    targetTime={dayjs(record?.deadline)}
                />
            ),
        },
        {
            title: t("warehouse.labels"),
            width: 140,
            dataIndex: "tags",
            render: (_, record) => (
                <WmsTags refId={record.requirementItemId} refType={ItemLabelEnum.REQUIREMENT_ITEM} tags={record?.requirementItem?.tags || []} />
            ),
        },
        {
            title: <SText text={t("sale.requirement")} />,
            dataIndex: ["requirementItem", "requirement", "number"],
            fixed: "right",
            width: 200,
            sorter: (a, b) => {
                const valA = a?.requirementItem?.requirement?.number ?? "";
                const valB = b?.requirementItem?.requirement?.number ?? "";
                return valA.localeCompare(valB);
            },
            render: (_, item) => (
                item?.requirementItem && item.requirementItem?.requirement ?
                    <Typography.Link
                        onClick={() => setRequirementDetail(item.requirementItem?.requirement || null)}
                    >
                        {item.requirementItem.requirement.number}
                    </Typography.Link>
                    : <Typography.Text type="secondary">{t("sale.notRequirement")}</Typography.Text>
            ),
        },
        {
            title: t("common.opt"),
            fixed: "right",
            width: 100,
            render: (_, record) => <Flex style={{ width: "100%" }} justify="space-evenly" gap={6}>
                {record.status === QuotationRequestStatusEnum.SUBMITED || record.status === QuotationRequestStatusEnum.REJECTED ? null
                    : activeTab === QuotationRequestInts.AUTO ? <Tooltip title={t('requirement.lazyQuota')}><Button onClick={() => setQutation(record)} icon={<FileSyncOutlined />} /></Tooltip>
                        : <Tooltip title={t('requirement.requirementHelp')}><Button onClick={() => setDetailId(record.id)} type="primary" icon={<EditOutlined />} /></Tooltip>}
                {hasPermission(curUser, [UserPermissionEnum.ADMIN]) && <Tooltip title={t("requirement.mrdEdit")}><Button onClick={() => setLazyMrdDetail(record)} icon={<FieldTimeOutlined />} /></Tooltip>}
                <Tooltip title={t('common.detail')}><Button onClick={() => setQuoDetail(record)} icon={<GroupOutlined />} /></Tooltip>
            </Flex>,
        },
    ]), [activeTab, curUser, getStatusLabel, t])

    const lazyQuotaCb = (newQuotation: PurchaseQuotationRequest | null) => {
        if (newQuotation) {
            setQutationList(last => {
                return last.map(item => {
                    if (item.id === newQuotation.id) return { ...item, deadline: newQuotation.deadline }
                    else return { ...item }
                })
            });
        }
        setQutation(null)
        return
    }

    const handleEnd = (detailId: string, type: 'submit' | 'reject', submitData?: QuotationRequestSubmit) => {
        if (detailId) {
            if (appliedStatusInts === SelectEnum.UN_PRICED) rmItem(detailId)
            else setQutationList(last => last.map(item => {
                if (item.id !== detailId) return { ...item };

                const status = type === 'submit' ? QuotationRequestStatusEnum.SUBMITED : QuotationRequestStatusEnum.REJECTED;
                if (!submitData) return { ...item, status };

                return { ...item, status, ...getQuotationSubmitPatch(submitData) };
            }))
        }
    }

    const lazyMrd = () => {
        setMrdLoading(true)
        let newTime = timeSelectorRef.current?.getFinalTime()
        quotationRequestUpdate(lazyMrdDetail?.id || '', { mrd: newTime || lazyMrdDetail?.mrd || 0 })
            .then(res => {
                setQutationList(last => {
                    return last.map(item => {
                        if (item.id === res.id) return { ...item, mrd: res.mrd }
                        else return { ...item }
                    })
                });
                notification.success({ placement: 'top', message: t('sale.updateSuccess') });
                setLazyMrdDetail(null);
            })
            .catch(() => {
                notification.error({ placement: 'top', message: t('order.modificatFailed') })
            })
            .finally(() => {
                setMrdLoading(false)
            })
    }

    const getSubmitData = useCallback((quotation: QuotationRequestItem) => {
        let res: QuotationRequestSubmit | null = null;
        let price = getSuppLadderPrice(quotation.price, quotation?.count)
        if (price) {
            let spareCount = quotation.spareCount || 0;
            let spareRate = multi(divided(spareCount + '', quotation?.count ?? '0'), '100')
            res = {
                supplierPrice: Number(price),
                supplierTime: quotation.mrd || dayjs().endOf('day').valueOf(),
                mpq: Number(quotation.price.mpq || '') || 1,
                days: quotation.days || 1,
                spareCount: spareCount + '',
                spareRate,
            }
            return res;
        } else return null;
    }, []);

    const batchSubmit = useCallback(async () => {
        if (selectedRowKeys.length === 0) return notification.error({ message: t('purchase.pleaseSelect') });
        setSubmiting(true);
        setSubmitProcess(0);
        try {
            let length = selectedRowKeys.length;
            let passItem: QuotationRequestItem[] = [];
            let handleId: string[] = [];
            const submitDataMap = new Map<string, QuotationRequestSubmit>();

            // 批量提交按选中的 id 在点击时拉详情，不把完整行对象放进选中 state。
            for (let i = 0; i < length; i++) {
                const id = selectedRowKeys[i];
                let quotation = await quotationRequestDetail(id);
                let data = getSubmitData(quotation);
                if (data) {
                    handleId.push(id)
                    submitDataMap.set(id, data)
                    await quotationRequestSubmit(id, data)
                } else passItem.push(quotation)
                setSubmitProcess(Math.round(((i + 1) / length) * 100))
            }
            if (appliedStatusInts === SelectEnum.UN_PRICED) {
                rmItems(handleId);
            } else setQutationList(last => {
                let resList: PurchaseQuotationRequest[] = last.map(item => {
                    const submitData = submitDataMap.get(item.id);
                    if (!submitData) return { ...item };

                    return {
                        ...item,
                        status: QuotationRequestStatusEnum.SUBMITED,
                        ...getQuotationSubmitPatch(submitData),
                    };
                })
                return [...resList]
            })
            notification.success({
                message: t('purchase.batchSubmitSucc'),
                description: passItem.length === 0 ? undefined
                    : <div>
                        <div>{t('purchase.needPriceTip')}</div>
                        {
                            passItem.map((item, index) => (
                                <div>{`${index + 1}、${t('purchase.code')}: ${item.supplier.code},${t('purchase.materialCode')}: ${item?.requirementItem?.product?.code ?? ''}`}</div>
                            ))
                        }
                    </div>
            })
            setSelectedRowKeys([])
        } catch (error) {
            notification.success({ message: t('purchase.batchSubmitErr') })
        } finally { setSubmiting(false) }
    }, [appliedStatusInts, getSubmitData, rmItems, selectedRowKeys, setQutationList, t]);

    return (
        <Flex vertical style={{ width: "100%", height: "100%" }}>
            <STableLayout
                rowKey={'id'}
                rowSelection={rowSelection}
                loading={activeListLoading}
                dataSource={qutationList}
                columns={columns as ColumnsType<AnyObject>}
                headerChildren={headerChildren}
                tableLayoutRef={tableRef}
                total={activeListTotal}
                handleChange={handleTableChange}
                scrollX={2780}
            />
            {detailId && <QuotationForm
                formKey={"cooperate"}
                detailId={detailId}
                onCancel={() => setDetailId("")}
                onSubmit={(id, submitData) => handleEnd(id || '', 'submit', submitData)}
                onReject={id => handleEnd(id || '', 'reject')}
            />}
            <QuotationForm formKey="detail" detailId={quoDetail?.id || ''} onCancel={() => setQuoDetail(null)} onSubmit={() => { }} onReject={() => { }} />

            {qutation && <LazyQuate quotation={qutation} cb={(newQutation) => lazyQuotaCb(newQutation as unknown as PurchaseQuotationRequest)} />}
            {requirementDetail && <RequirementDetail
                id={requirementDetail.id}
                number={requirementDetail.number}
                open={!!requirementDetail}
                onCancel={() => setRequirementDetail(null)}
                handleCancel={() => setRequirementDetail(null)}
                handleOk={() => setRequirementDetail(null)}
            />}
            {lazyMrdDetail && lazyMrdDetail.mrd && <Modal title={t('requirement.mrdEdit')}
                open={true} onOk={lazyMrd} onCancel={() => setLazyMrdDetail(null)} okButtonProps={{ loading: mrdLoading }}
                width={600}> <TimeSelector curTime={lazyMrdDetail.mrd} curTimeTip={t("requirement.curMrd")} finalTimeTip={t("requirement.finalMrd")} changeTip={t("requirement.selMrd")} ref={timeSelectorRef} />
            </Modal>}
            <ExportProgressModal visible={exportLoading} progress={exportProgress} />
            <ExportProgressModal title={t('purchase.batchSubmit')} processLabel={t('purchase.batchSubmit')} visible={submiting} progress={submitProcess} />
        </Flex>
    );
};

export default RequirementList;
